// client/src/pages/ProfileSettingsPage.js
import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import * as authApi from '../api/auth'; // Import auth API service for profile update

function ProfileSettingsPage({ inTab = false }) {
  const { user, loading: authLoading, setUser } = useAuth(); // Get user, loading, and setUser from context
  
  const [formData, setFormData] = useState({
    companyName: '',
    companyAddress: '',
    companyPhone: '',
    defaultTermsConditions: '',
  });
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [loading, setLoading] = useState(false); // Local loading state for form submission
  const [logoFile, setLogoFile] = useState(null); // Selected logo file
  const [logoPreview, setLogoPreview] = useState(null); // Preview URL for selected logo
  const [uploadingLogo, setUploadingLogo] = useState(false); // Loading state for logo upload

  // Effect to parse URL parameters for OAuth redirect messages
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('oauth_success') === 'true') {
      setSuccess('Gmail account connected successfully!');
      // Optionally, refetch user data to update isGmailConnected status immediately
      const fetchUpdatedUser = async () => {
          try {
              const updatedUser = await authApi.fetchCurrentUser();
              setUser(updatedUser.user);
          } catch (err) {
              console.error("Failed to refetch user after OAuth success:", err);
          }
      };
      fetchUpdatedUser();
    } else if (params.get('oauth_error')) {
      setError(`Gmail connection failed: ${decodeURIComponent(params.get('oauth_error'))}`);
    }
    // Clear the URL parameters to avoid showing message on subsequent visits/refreshes
    window.history.replaceState({}, document.title, window.location.pathname);
  }, [setUser]); // Dependencies ensure this runs when setUser changes

  // Populate form when user data is available
  useEffect(() => {
    if (user) {
      setFormData({
        companyName: user.companyName || '', // Use user.companyName from context (backend returns camelCase)
        companyAddress: user.companyAddress || '',
        companyPhone: user.companyPhone || '',
        defaultTermsConditions: user.defaultTermsConditions || '',
      });
    }
  }, [user, authLoading]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setLoading(true);

    try {
      // Filter out empty string values to only send non-empty fields
      const dataToSend = Object.keys(formData).reduce((acc, key) => {
        if (formData[key] && formData[key].trim) {
          // For string fields, trim and only include if non-empty
          const trimmedValue = formData[key].trim();
          if (trimmedValue) {
            acc[key] = trimmedValue;
          }
        } else if (formData[key]) {
          // For non-string fields (like textarea without trim), include if truthy
          acc[key] = formData[key];
        }
        return acc;
      }, {});

      // Call the updateProfile API function from auth.js
      // JWT token is stored in HTTP-only cookies, so credentials: 'include' handles auth
      const response = await authApi.updateProfile(dataToSend);

      // Convert snake_case response keys to camelCase for consistency
      const camelCaseUser = {
        ...response.user,
        companyName: response.user.company_name || response.user.companyName,
        companyAddress: response.user.company_address || response.user.companyAddress,
        companyPhone: response.user.company_phone || response.user.companyPhone,
        defaultTermsConditions: response.user.default_terms_conditions || response.user.defaultTermsConditions,
        companyLogoUrl: response.user.company_logo_url || response.user.companyLogoUrl,
        onboardingStatus: response.user.onboarding_status || response.user.onboardingStatus,
        currentOnboardingPhase: response.user.current_onboarding_phase || response.user.currentOnboardingPhase,
        phase1CompletionCelebrated: response.user.phase1_completion_celebrated || response.user.phase1CompletionCelebrated,
        isGmailConnected: response.user.isGmailConnected
      };

      // Update the user object in AuthContext state to reflect changes immediately
      setUser(camelCaseUser);

      // Also update local formData to ensure UI reflects saved data immediately
      setFormData({
        companyName: camelCaseUser.companyName || '',
        companyAddress: camelCaseUser.companyAddress || '',
        companyPhone: camelCaseUser.companyPhone || '',
        defaultTermsConditions: camelCaseUser.defaultTermsConditions || '',
      });

      setSuccess('Profile updated successfully!');
    } catch (err) {
      setError(err.message || 'Failed to update profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handler for logo file selection
  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setError('Please select an image file.');
        return;
      }
      // Validate file size (5MB max)
      if (file.size > 5 * 1024 * 1024) {
        setError('Logo file size must be less than 5MB.');
        return;
      }
      setLogoFile(file);
      // Create preview URL
      const previewUrl = URL.createObjectURL(file);
      setLogoPreview(previewUrl);
      setError(null);
    }
  };

  // Handler for uploading logo
  const handleLogoUpload = async () => {
    if (!logoFile) {
      setError('Please select a logo file first.');
      return;
    }

    setError(null);
    setSuccess(null);
    setUploadingLogo(true);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('logo', logoFile);

      const response = await fetch('/api/users/logo', {
        method: 'POST',
        credentials: 'include',
        body: formDataToSend,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to upload logo.');
      }

      const data = await response.json();

      // Update user context with new logo URL
      setUser({ ...user, companyLogoUrl: data.logoUrl });

      // Clear the file input and preview
      setLogoFile(null);
      setLogoPreview(null);

      setSuccess('Logo uploaded successfully!');
    } catch (err) {
      console.error('Error uploading logo:', err);
      setError(err.message || 'Failed to upload logo. Please try again.');
    } finally {
      setUploadingLogo(false);
    }
  };

  // Handler for deleting logo
  const handleLogoDelete = async () => {
    if (!user.companyLogoUrl) {
      return;
    }

    setError(null);
    setSuccess(null);
    setUploadingLogo(true);

    try {
      const response = await fetch('/api/users/logo', {
        method: 'DELETE',
        credentials: 'include',
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete logo.');
      }

      // Update user context to remove logo URL
      setUser({ ...user, companyLogoUrl: null });

      setSuccess('Logo deleted successfully!');
    } catch (err) {
      console.error('Error deleting logo:', err);
      setError(err.message || 'Failed to delete logo. Please try again.');
    } finally {
      setUploadingLogo(false);
    }
  };

  // Handler for connecting Gmail account
 const handleConnectGmail = async () => { // Make sure this is 'async'
    setError(null);
    setSuccess(null);
    setLoading(true);

    try {
        // IMPORTANT: This fetches from /api/users/auth/google-init
        // which is protected and returns the actual Google OAuth URL.
        const response = await fetch('/api/users/auth/google-init', {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json'
            },
            credentials: 'include',
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || 'Failed to get Google OAuth URL.');
        }

        const { authUrl } = await response.json(); // Backend will return { authUrl: "..." }
        window.location.href = authUrl; // Now, redirect the browser to Google's consent screen

    } catch (err) {
        console.error('Error connecting Gmail:', err);
        setError(err.message || 'Failed to connect Gmail account. Please try again.');
    } finally {
        setLoading(false);
    }
};

// Handler for disconnecting Gmail account
const handleDisconnectGmail = async () => {
    setError(null);
    setSuccess(null);
    setLoading(true);

    try {
        const response = await fetch('/api/users/auth/gmail', {
            method: 'DELETE',
            headers: {
                'Content-Type': 'application/json'
            },
            credentials: 'include',
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || 'Failed to disconnect Gmail account.');
        }

        const data = await response.json();

        // Update user context
        setUser({ ...user, isGmailConnected: false });

        setSuccess('Gmail account disconnected successfully!');
    } catch (err) {
        console.error('Error disconnecting Gmail:', err);
        setError(err.message || 'Failed to disconnect Gmail account. Please try again.');
    } finally {
        setLoading(false);
    }
};

  if (authLoading || !user) {
    return (
      <div className="min-h-[calc(100vh-64px)] flex items-center justify-center text-lg text-gray-700">
        Loading profile...
      </div>
    );
  }

  if (inTab) {
    // Tab view - no wrapper, no title
    return (
      <div className="w-full max-w-full">
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-md relative mb-4 text-sm sm:text-base" role="alert">
            <span className="block sm:inline">{error}</span>
          </div>
        )}
        {success && (
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded-md relative mb-4 text-sm sm:text-base" role="alert">
            <span className="block sm:inline">{success}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4 w-full max-w-full">
          <div className="pt-4 border-t border-gray-200">
            <h3 className="text-xl font-semibold text-gray-800 mb-3">Company Details</h3>
            <div>
              <label htmlFor="companyName" className="block text-sm font-medium text-gray-700 mb-1">Company Name</label>
              <input
                type="text"
                id="companyName"
                name="companyName"
                value={formData.companyName}
                onChange={handleChange}
                placeholder="Your Company Inc."
                className="mt-1 block w-full p-3 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
              />
            </div>
            <div>
              <label htmlFor="companyAddress" className="block text-sm font-medium text-gray-700 mb-1">Company Address</label>
              <input
                type="text"
                id="companyAddress"
                name="companyAddress"
                value={formData.companyAddress}
                onChange={handleChange}
                placeholder="123 Business Rd, City, State, Zip"
                className="mt-1 block w-full p-3 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
              />
            </div>
            <div>
              <label htmlFor="companyPhone" className="block text-sm font-medium text-gray-700 mb-1">Company Phone</label>
              <input
                type="tel"
                id="companyPhone"
                name="companyPhone"
                value={formData.companyPhone}
                onChange={handleChange}
                placeholder="555-123-4567"
                className="mt-1 block w-full p-3 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
              />
            </div>
          </div>

          {/* Logo Upload Section */}
          <div className="pt-4 border-t border-gray-200">
            <h3 className="text-xl font-semibold text-gray-800 mb-3">Company Logo</h3>
            <p className="text-sm text-gray-600 mb-4">Upload a logo to display on your quotes and invoices. Recommended size: 200x200px or larger. Max file size: 5MB.</p>

            <div className="space-y-4 w-full max-w-full">
              {/* Current Logo Display */}
              {user.companyLogoUrl && !logoPreview && (
                <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4 space-y-3 sm:space-y-0 min-w-0">
                  <img
                    src={user.companyLogoUrl}
                    alt="Current Company Logo"
                    className="w-32 h-32 object-contain border border-gray-300 rounded-md p-2 bg-white flex-shrink-0"
                  />
                  <button
                    type="button"
                    onClick={handleLogoDelete}
                    disabled={uploadingLogo}
                    className="px-4 py-2 bg-red-600 text-white font-medium rounded-md shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition duration-300 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                  >
                    {uploadingLogo ? 'Deleting...' : 'Delete Logo'}
                  </button>
                </div>
              )}

              {/* Logo Preview */}
              {logoPreview && (
                <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4 space-y-3 sm:space-y-0 min-w-0">
                  <img
                    src={logoPreview}
                    alt="Logo Preview"
                    className="w-32 h-32 object-contain border border-gray-300 rounded-md p-2 bg-white flex-shrink-0"
                  />
                  <div className="flex flex-col space-y-2">
                    <button
                      type="button"
                      onClick={handleLogoUpload}
                      disabled={uploadingLogo}
                      className="px-4 py-2 bg-green-600 text-white font-medium rounded-md shadow-sm hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {uploadingLogo ? 'Uploading...' : 'Upload Logo'}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setLogoFile(null);
                        setLogoPreview(null);
                      }}
                      disabled={uploadingLogo}
                      className="px-4 py-2 bg-gray-400 text-white font-medium rounded-md shadow-sm hover:bg-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400 transition duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {/* File Input */}
              <div>
                <label
                  htmlFor="logoUpload"
                  className="block w-full px-4 py-3 border-2 border-dashed border-gray-300 rounded-md text-center cursor-pointer hover:border-primary-500 transition duration-300"
                >
                  <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48" aria-hidden="true">
                    <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <span className="mt-2 block text-sm font-medium text-gray-700">
                    {logoFile ? logoFile.name : 'Click to select a logo image'}
                  </span>
                </label>
                <input
                  id="logoUpload"
                  name="logoUpload"
                  type="file"
                  accept="image/*"
                  onChange={handleLogoChange}
                  className="hidden"
                  disabled={uploadingLogo}
                />
              </div>
            </div>
          </div>

          <div className="pt-4 border-t border-gray-200">
            <h3 className="text-xl font-semibold text-gray-800 mb-3">Default Terms & Conditions</h3>
            <div>
              <label htmlFor="defaultTermsConditions" className="block text-sm font-medium text-gray-700 mb-1">
                These terms will pre-fill new quotes.
              </label>
              <textarea
                id="defaultTermsConditions"
                name="defaultTermsConditions"
                value={formData.defaultTermsConditions}
                onChange={handleChange}
                rows="6"
                placeholder="E.g., Payment due upon completion, Scope of work details..."
                className="mt-1 block w-full p-3 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
              ></textarea>
            </div>
          </div>

          <button
            type="submit"
            className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-lg font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition duration-300"
            disabled={loading || authLoading}
          >
            {loading || authLoading ? (
              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            ) : (
              'Save Profile Settings'
            )}
          </button>
        </form>

        {/* Google / Gmail Account Connection Section */}
        <div className="mt-8 pt-6 border-t border-gray-200">
            <h3 className="text-xl font-semibold text-gray-800 mb-3">Gmail Connect</h3>
            {user?.isGmailConnected ? (
                <div className="space-y-3">
                    <div className="text-green-600 font-medium flex items-center justify-center space-x-2">
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
                        <span>Gmail Connected!</span>
                    </div>
                    <button
                        onClick={handleDisconnectGmail}
                        className="w-full px-6 py-2 bg-red-600 text-white font-medium rounded-lg shadow-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                        disabled={loading || authLoading}
                    >
                        {loading || authLoading ? (
                           <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white inline" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                        ) : (
                          'Disconnect Gmail Account'
                        )}
                    </button>
                </div>
            ) : (
                <div>
                    <p className="text-gray-600 mb-4">Connect your Gmail account to send quotes directly via email.</p>
                    <button
                        onClick={handleConnectGmail}
                        className="w-full px-6 py-3 bg-blue-600 text-white font-medium rounded-lg shadow-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                        disabled={loading || authLoading}
                    >
                        {loading || authLoading ? (
                           <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white inline" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                        ) : (
                          'Connect Gmail Account'
                        )}
                    </button>
                </div>
            )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-64px)] flex items-center justify-center bg-gray-50 p-4 sm:p-6">
      <div className="bg-white p-6 sm:p-8 rounded-xl shadow-lg border border-gray-200 w-full max-w-xl">
        <h2 className="text-3xl font-bold text-primary-700 mb-6 text-center">Manage Your Profile & Company Settings</h2>
        
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-md relative mb-4" role="alert">
            <span className="block sm:inline">{error}</span>
          </div>
        )}
        {success && (
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded-md relative mb-4" role="alert">
            <span className="block sm:inline">{success}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="pt-4 border-t border-gray-200">
            <h3 className="text-xl font-semibold text-gray-800 mb-3">Company Details</h3>
            <div>
              <label htmlFor="companyName" className="block text-sm font-medium text-gray-700 mb-1">Company Name</label>
              <input
                type="text"
                id="companyName"
                name="companyName"
                value={formData.companyName}
                onChange={handleChange}
                placeholder="Your Company Inc."
                className="mt-1 block w-full p-3 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
              />
            </div>
            <div>
              <label htmlFor="companyAddress" className="block text-sm font-medium text-gray-700 mb-1">Company Address</label>
              <input
                type="text"
                id="companyAddress"
                name="companyAddress"
                value={formData.companyAddress}
                onChange={handleChange}
                placeholder="123 Business Rd, City, State, Zip"
                className="mt-1 block w-full p-3 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
              />
            </div>
            <div>
              <label htmlFor="companyPhone" className="block text-sm font-medium text-gray-700 mb-1">Company Phone</label>
              <input
                type="tel"
                id="companyPhone"
                name="companyPhone"
                value={formData.companyPhone}
                onChange={handleChange}
                placeholder="555-123-4567"
                className="mt-1 block w-full p-3 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
              />
            </div>
          </div>

          {/* Logo Upload Section */}
          <div className="pt-4 border-t border-gray-200">
            <h3 className="text-xl font-semibold text-gray-800 mb-3">Company Logo</h3>
            <p className="text-sm text-gray-600 mb-4">Upload a logo to display on your quotes and invoices. Recommended size: 200x200px or larger. Max file size: 5MB.</p>

            <div className="space-y-4 w-full max-w-full">
              {/* Current Logo Display */}
              {user.companyLogoUrl && !logoPreview && (
                <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4 space-y-3 sm:space-y-0 min-w-0">
                  <img
                    src={user.companyLogoUrl}
                    alt="Current Company Logo"
                    className="w-32 h-32 object-contain border border-gray-300 rounded-md p-2 bg-white flex-shrink-0"
                  />
                  <button
                    type="button"
                    onClick={handleLogoDelete}
                    disabled={uploadingLogo}
                    className="px-4 py-2 bg-red-600 text-white font-medium rounded-md shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition duration-300 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                  >
                    {uploadingLogo ? 'Deleting...' : 'Delete Logo'}
                  </button>
                </div>
              )}

              {/* Logo Preview */}
              {logoPreview && (
                <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4 space-y-3 sm:space-y-0 min-w-0">
                  <img
                    src={logoPreview}
                    alt="Logo Preview"
                    className="w-32 h-32 object-contain border border-gray-300 rounded-md p-2 bg-white flex-shrink-0"
                  />
                  <div className="flex flex-col space-y-2">
                    <button
                      type="button"
                      onClick={handleLogoUpload}
                      disabled={uploadingLogo}
                      className="px-4 py-2 bg-green-600 text-white font-medium rounded-md shadow-sm hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {uploadingLogo ? 'Uploading...' : 'Upload Logo'}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setLogoFile(null);
                        setLogoPreview(null);
                      }}
                      disabled={uploadingLogo}
                      className="px-4 py-2 bg-gray-400 text-white font-medium rounded-md shadow-sm hover:bg-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400 transition duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {/* File Input */}
              <div>
                <label
                  htmlFor="logoUpload"
                  className="block w-full px-4 py-3 border-2 border-dashed border-gray-300 rounded-md text-center cursor-pointer hover:border-primary-500 transition duration-300"
                >
                  <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48" aria-hidden="true">
                    <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <span className="mt-2 block text-sm font-medium text-gray-700">
                    {logoFile ? logoFile.name : 'Click to select a logo image'}
                  </span>
                </label>
                <input
                  id="logoUpload"
                  name="logoUpload"
                  type="file"
                  accept="image/*"
                  onChange={handleLogoChange}
                  className="hidden"
                  disabled={uploadingLogo}
                />
              </div>
            </div>
          </div>

          <div className="pt-4 border-t border-gray-200">
            <h3 className="text-xl font-semibold text-gray-800 mb-3">Default Terms & Conditions</h3>
            <div>
              <label htmlFor="defaultTermsConditions" className="block text-sm font-medium text-gray-700 mb-1">
                These terms will pre-fill new quotes.
              </label>
              <textarea
                id="defaultTermsConditions"
                name="defaultTermsConditions"
                value={formData.defaultTermsConditions}
                onChange={handleChange}
                rows="6"
                placeholder="E.g., Payment due upon completion, Scope of work details..."
                className="mt-1 block w-full p-3 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
              ></textarea>
            </div>
          </div>
          
          <button
            type="submit"
            className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-lg font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition duration-300"
            disabled={loading || authLoading}
          >
            {loading || authLoading ? (
              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            ) : (
              'Save Profile Settings'
            )}
          </button>
        </form>

        {/* Google / Gmail Account Connection Section */}
        <div className="mt-8 pt-6 border-t border-gray-200">
            <h3 className="text-xl font-semibold text-gray-800 mb-3">Gmail Connect</h3>
            {user?.isGmailConnected ? (
                <div className="space-y-3">
                    <div className="text-green-600 font-medium flex items-center justify-center space-x-2">
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
                        <span>Gmail Connected!</span>
                    </div>
                    <button
                        onClick={handleDisconnectGmail}
                        className="w-full px-6 py-2 bg-red-600 text-white font-medium rounded-lg shadow-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                        disabled={loading || authLoading}
                    >
                        {loading || authLoading ? (
                           <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white inline" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                        ) : (
                          'Disconnect Gmail Account'
                        )}
                    </button>
                </div>
            ) : (
                <div>
                    <p className="text-gray-600 mb-4">Connect your Gmail account to send quotes directly via email.</p>
                    <button
                        onClick={handleConnectGmail}
                        className="w-full px-6 py-3 bg-blue-600 text-white font-medium rounded-lg shadow-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                        disabled={loading || authLoading}
                    >
                        {loading || authLoading ? (
                           <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white inline" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                        ) : (
                          'Connect Gmail Account'
                        )}
                    </button>
                </div>
            )}
        </div>
      </div>
    </div>
  );
}

export default ProfileSettingsPage;
